/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import SecureLS from 'secure-ls'
import jwtDecode from 'jwt-decode'

const Auth0Context = createContext({
  isAuthenticated: false,
  user: null,
  error: null,
  loginWithRedirect: async () => {},
  logout: () => {},
  getAccessTokenSilently: async () => null,
  setToken: () => null,
})

const safeDecode = (token) => {
  if (!token) {
    return null
  }
  try {
    return jwtDecode(token)
  } catch (error) {
    console.warn('[auth0-react] Failed to decode token:', error)
    return null
  }
}

const encodeBase64 = (value) => {
  if (typeof btoa === 'function') {
    return btoa(value)
  }
  if (typeof globalThis !== 'undefined' && typeof globalThis.Buffer !== 'undefined') {
    return globalThis.Buffer.from(value, 'utf8').toString('base64')
  }
  return value
}

const persistState = (storage, key, value) => {
  try {
    if (value === null || value === undefined) {
      storage.remove(key)
      return
    }
    storage.set(key, value)
  } catch (error) {
    console.warn('[auth0-react] Could not persist state:', error)
  }
}

export function Auth0Provider({
  children,
  domain,
  clientId,
  authorizationParams = {},
  cacheLocation = 'localstorage',
  useRefreshTokens = false,
  onRedirectCallback,
}) {
  const storage = useMemo(
    () =>
      new SecureLS({
        prefix: `auth0:${domain ?? 'app'}:${clientId ?? 'client'}:`,
        encodingType: cacheLocation === 'localstorage' ? 'base64' : 'memory',
      }),
    [cacheLocation, clientId, domain],
  )

  const [tokenState, setTokenState] = useState(() => storage.get('token') ?? null)
  const [userState, setUserState] = useState(() => storage.get('user') ?? null)
  const [errorState, setErrorState] = useState(null)

  useEffect(() => {
    persistState(storage, 'token', tokenState)
  }, [storage, tokenState])

  useEffect(() => {
    persistState(storage, 'user', userState)
  }, [storage, userState])

  const getAccessTokenSilently = useCallback(async () => tokenState?.accessToken ?? null, [tokenState])

  const loginWithRedirect = useCallback(
    async (options = {}) => {
      const syntheticToken = options.accessToken ?? tokenState?.accessToken ?? null
      if (!syntheticToken) {
        const payload = encodeBase64(
          JSON.stringify({
            sub: 'demo-user',
            email: 'demo@example.com',
            'https://eduvera.ch/roles': ['guest'],
          }),
        )
        const header = encodeBase64(JSON.stringify({ alg: 'none', typ: 'JWT' }))
        const signature = 'demo'
        const fallbackToken = `${header}.${payload}.${signature}`
        const decoded = safeDecode(fallbackToken)
        setTokenState({ accessToken: fallbackToken, payload: decoded })
        setUserState(decoded)
        return decoded
      }

      const decoded = options.payload ?? safeDecode(syntheticToken)
      if (!decoded) {
        setErrorState(new Error('Ungültiges Authentifizierungs-Token'))
        return null
      }

      setTokenState({ accessToken: syntheticToken, payload: decoded })
      setUserState(decoded)

      if (typeof onRedirectCallback === 'function') {
        onRedirectCallback(decoded, options.appState ?? {})
      }

      return decoded
    },
    [onRedirectCallback, tokenState?.accessToken],
  )

  const logout = useCallback(
    ({ returnTo } = {}) => {
      setTokenState(null)
      setUserState(null)
      storage.remove('token')
      storage.remove('user')
      if (returnTo && typeof window !== 'undefined') {
        window.location.assign(returnTo)
      }
    },
    [storage],
  )

  const setToken = useCallback(
    (rawToken) => {
      const decoded = safeDecode(rawToken)
      if (!decoded) {
        setErrorState(new Error('Token konnte nicht validiert werden.'))
        return null
      }
      const record = { accessToken: rawToken, payload: decoded }
      setTokenState(record)
      setUserState(decoded)
      return record
    },
    [],
  )

  const value = useMemo(
    () => ({
      isAuthenticated: Boolean(tokenState?.accessToken),
      user: userState,
      error: errorState,
      loginWithRedirect,
      logout,
      getAccessTokenSilently,
      authorizationParams,
      useRefreshTokens,
      setToken,
    }),
    [
      authorizationParams,
      errorState,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
      setToken,
      tokenState?.accessToken,
      useRefreshTokens,
      userState,
    ],
  )

  return <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>
}

Auth0Provider.displayName = 'Auth0Provider'

export const useAuth0 = () => useContext(Auth0Context)

export function withAuthenticationRequired(WrappedComponent) {
  return function WithAuthenticationRequired(props) {
    const { isAuthenticated, loginWithRedirect } = useAuth0()
    const ComponentToRender = WrappedComponent

    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: typeof window !== 'undefined' ? window.location.pathname : '/' } })
      return null
    }

    return <ComponentToRender {...props} />
  }
}

export default Auth0Provider
